import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import Modal from '../Modal'
import { confirmGanttImport, downloadGanttTemplate, previewGanttImport } from '../../api/client'

const ACTION_STYLES = {
  create: 'bg-emerald-50 text-emerald-700',
  update: 'bg-amber-50 text-amber-700',
  unchanged: 'bg-gray-50 text-gray-400',
  error: 'bg-red-50 text-red-700',
}

function Counter({ label, value, tone = 'text-gray-900' }) {
  return (
    <div className="flex-1 rounded-lg border border-gray-200 px-3 py-2">
      <div className={`text-xl font-semibold tabular-nums ${tone}`}>{value}</div>
      <div className="text-[11px] text-gray-500">{label}</div>
    </div>
  )
}

export default function GanttImportModal({ projectId, projectName, onClose, onImported }) {
  const { t } = useTranslation(['engineering', 'common'])
  const [step, setStep] = useState('upload')   // upload | preview | done
  const [preview, setPreview] = useState(null)
  const [deleteMissing, setDeleteMissing] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [result, setResult] = useState(null)
  const fileRef = useRef()

  const k = (suffix) => t(`projectDetail.gantt.import.${suffix}`)

  const steps = [
    ['upload', k('stepUpload')],
    ['preview', k('stepPreview')],
    ['done', k('stepDone')],
  ]

  const handleFile = async (event) => {
    const file = event.target.files?.[0]
    if (!file) return
    setBusy(true)
    setError('')
    try {
      const data = await previewGanttImport(projectId, file)
      setPreview(data)
      setStep('preview')
    } catch (err) {
      setError(err.response?.data?.detail || k('failed'))
    } finally {
      setBusy(false)
      if (fileRef.current) fileRef.current.value = ''
    }
  }

  const handleConfirm = async () => {
    setBusy(true)
    setError('')
    try {
      const data = await confirmGanttImport(projectId, preview.raw, deleteMissing)
      setResult(data)
      setStep('done')
      onImported?.()
    } catch (err) {
      setError(err.response?.data?.detail || k('failed'))
    } finally {
      setBusy(false)
    }
  }

  const summary = preview?.summary
  const hasErrors = (summary?.errors || 0) > 0
  const nothingToDo = summary && !summary.created && !summary.updated && !summary.errors
  // Deleting is the one destructive path here, so it stays available even
  // when the file itself brings no other change.
  const canApply = !hasErrors && (!nothingToDo || (deleteMissing && preview.missing_in_file.length > 0))

  return (
    <Modal title={k('title')} onClose={onClose}>
      <div className="space-y-4" style={{ minWidth: 'min(760px, 80vw)' }}>
        {/* Step indicator */}
        <div className="flex items-center gap-2 text-xs">
          {steps.map(([key, label], i) => {
            const active = step === key
            const passed = steps.findIndex(s => s[0] === step) > i
            return (
              <div key={key} className="flex items-center gap-2">
                <span className={`px-2 py-1 rounded-full ${
                  active ? 'bg-gray-900 text-white'
                    : passed ? 'bg-emerald-50 text-emerald-700' : 'bg-gray-100 text-gray-400'}`}>
                  {label}
                </span>
                {i < steps.length - 1 && <span className="text-gray-300">›</span>}
              </div>
            )
          })}
        </div>

        {error && <p className="text-red-600 text-sm bg-red-50 px-3 py-2 rounded-lg">{error}</p>}

        {step === 'upload' && (
          <div className="space-y-3">
            <p className="text-sm text-gray-600">{k('intro')}</p>
            <div className="flex gap-3">
              <button
                onClick={() => downloadGanttTemplate(projectId, projectName)}
                className="px-3 py-2 rounded-lg text-sm font-medium border border-gray-200 hover:bg-gray-50"
              >
                {t('projectDetail.gantt.downloadTemplate')}
              </button>
              <button
                onClick={() => fileRef.current?.click()}
                disabled={busy}
                className="px-4 py-2 rounded-lg text-sm font-medium bg-gray-900 text-white hover:bg-gray-800 disabled:opacity-50"
              >
                {busy ? k('parsing') : k('chooseFile')}
              </button>
              <input ref={fileRef} type="file" accept=".xlsx" className="hidden" onChange={handleFile} />
            </div>
          </div>
        )}

        {step === 'preview' && preview && (
          <div className="space-y-3">
            <div className="flex gap-2">
              <Counter label={k('created')} value={summary.created} tone="text-emerald-700" />
              <Counter label={k('updated')} value={summary.updated} tone="text-amber-700" />
              <Counter label={k('unchanged')} value={summary.unchanged} tone="text-gray-400" />
              <Counter label={k('errors')} value={summary.errors} tone={hasErrors ? 'text-red-600' : 'text-gray-400'} />
              <Counter label={k('phasesCreated')} value={summary.phases_created} />
            </div>

            {preview.members_not_found.length > 0 && (
              <p className="text-xs text-amber-700 bg-amber-50 px-3 py-2 rounded-lg">
                {k('membersNotFound').replace('{{names}}', preview.members_not_found.join(', '))}
              </p>
            )}

            {/* Only what changes: an unchanged row is noise here. */}
            <div className="max-h-64 overflow-auto rounded-lg border border-gray-200">
              <table className="w-full text-xs">
                <tbody>
                  {preview.rows.filter(r => r.action !== 'unchanged').map(r => (
                    <tr key={r.row} className="border-b border-gray-100 last:border-0">
                      <td className="px-2 py-1.5 text-gray-400 whitespace-nowrap w-16">
                        {k('rowLabel').replace('{{row}}', r.row)}
                      </td>
                      <td className="px-2 py-1.5 w-20">
                        <span className={`px-1.5 py-0.5 rounded ${ACTION_STYLES[r.action]}`}>
                          {k(r.action === 'error' ? 'errors' : r.action)}
                        </span>
                      </td>
                      <td className="px-2 py-1.5 text-gray-400 w-16">{r.phase || '—'}</td>
                      <td className="px-2 py-1.5 text-gray-700">{r.name || '—'}</td>
                      <td className="px-2 py-1.5 text-red-600">
                        {r.errors.map(e => `${e.field}: ${e.message}`).join(' · ')}
                      </td>
                    </tr>
                  ))}
                  {preview.rows.every(r => r.action === 'unchanged') && (
                    <tr><td className="px-3 py-4 text-center text-gray-400">{k('nothingToDo')}</td></tr>
                  )}
                </tbody>
              </table>
            </div>

            {preview.missing_in_file.length > 0 && (
              <div className="rounded-lg border border-gray-200 px-3 py-2 space-y-1">
                <p className="text-xs text-gray-600">
                  {k('missingInFile').replace('{{count}}', preview.missing_in_file.length)}
                </p>
                <label className="flex items-center gap-2 text-xs text-gray-700">
                  <input type="checkbox" checked={deleteMissing}
                         onChange={e => setDeleteMissing(e.target.checked)} />
                  {k('deleteMissing')}
                </label>
                <p className="text-[11px] text-gray-400">{k('deleteMissingHint')}</p>
              </div>
            )}

            {hasErrors && (
              <p className="text-xs text-red-600 bg-red-50 px-3 py-2 rounded-lg">{k('fixErrors')}</p>
            )}

            <div className="flex gap-3 pt-1">
              <button
                onClick={handleConfirm}
                disabled={busy || !canApply}
                className="flex-1 bg-gray-900 text-white py-2 rounded-lg text-sm font-medium hover:bg-gray-800 disabled:opacity-40"
              >
                {busy ? k('applying') : k('confirm')}
              </button>
              <button
                onClick={() => { setStep('upload'); setPreview(null) }}
                className="flex-1 bg-gray-100 text-gray-700 py-2 rounded-lg text-sm font-medium hover:bg-gray-200"
              >
                {t('common:actions.cancel')}
              </button>
            </div>
          </div>
        )}

        {step === 'done' && result && (
          <div className="space-y-4">
            <p className="text-sm text-gray-700">
              {k('doneMessage')
                .replace('{{created}}', result.summary.created)
                .replace('{{updated}}', result.summary.updated)}
            </p>
            <button
              onClick={onClose}
              className="w-full bg-gray-900 text-white py-2 rounded-lg text-sm font-medium hover:bg-gray-800"
            >
              {k('close')}
            </button>
          </div>
        )}
      </div>
    </Modal>
  )
}
